import { Link } from "react-router-dom";
import { ShieldCheck, FileText, ClipboardList, AlertTriangle, Banknote, Compass, ArrowRight } from "lucide-react";
import { PageHero, SectionHeading, DisclaimerBar } from "../components/shared/Primitives";
import { useHandoverSession } from "../hooks/useHandoverSession";

const SECTIONS = [
  { icon: Compass, title: "Before taking private clients", body: "The groundwork that protects you: understanding self-employment, business basics, insurance, and what \"professional\" actually looks like to a family." },
  { icon: Banknote, title: "Getting paid safely", body: "Deposits, invoicing, payment terms, and what to do when a client is late. Clarity beats chasing every time." },
  { icon: ShieldCheck, title: "Setting boundaries", body: "Scope, hours, communication and saying no without losing work. Burnout is a red flag, not a badge." },
  { icon: FileText, title: "Using contracts and written agreements", body: "A short agreement covers 95% of disputes before they start. We give you the templates and the plain-English version." },
  { icon: ClipboardList, title: "Recording care properly", body: "Arrival evidence, daily logs, medication records, handovers and incident reports — the same standard as regulated care." },
  { icon: AlertTriangle, title: "Spotting red flags", body: "Warning signs from families, from other carers, and in arrangements that look good on paper but aren't." },
];

export default function ForCarers() {
  const session = useHandoverSession();
  const hasCarerFree = session.ready && session.active && session.entitlements.includes("carer_free");

  return (
    <div data-testid="page-for-carers">
      <PageHero
        eyebrow="For Carers"
        title="Work independently, professionally and protected."
        description="Support, structure and practical tools for carers who want to work independently — professionally, safely and with confidence."
        accent="purple"
        bgTint="carer"
      >
        {hasCarerFree ? (
          <Link
            to="/workspace/carer-starter"
            className="btn-purple"
            data-testid="carers-cta-free"
          >
            Go to Carer Starter
          </Link>
        ) : (
          <a
            href="/join/carer-starter"
            className="btn-purple"
            data-testid="carers-cta-free"
          >
            Join Free Carer Access
          </a>
        )}
        <Link to="/pro-carer-circle" className="btn-outline">Explore Pro Carer Office</Link>
      </PageHero>

      <section className="section">
        <div className="container-jcc">
          <SectionHeading eyebrow="What we cover" accent="purple"
            title="The things independent carers actually need help with."
            description="Use what helps your situation. Everything is designed to be practical, usable and grounded in real care work." />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SECTIONS.map((s) => (
              <div key={s.title} className="card-base p-7 hover:-translate-y-1 transition-transform duration-300" data-testid={`carer-pillar-${s.title.toLowerCase().split(' ').slice(0,2).join('-')}`}>
                <div className="w-11 h-11 rounded-xl bg-purple-50 text-purple flex items-center justify-center mb-4">
                  <s.icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-display font-semibold text-xl">{s.title}</h3>
                <p className="mt-2 text-ink-muted text-sm leading-relaxed">{s.body}</p>
              </div>
            ))}
            <div className="card-base p-7 bg-surface-carer border-purple-100">
              <h3 className="font-display font-semibold text-xl text-purple">Moving from agency to independent</h3>
              <p className="mt-2 text-ink-muted text-sm leading-relaxed">A structured plan: giving notice, transitioning clients ethically, pricing, insurance, tax registration and the admin routine that keeps it all running.</p>
              <Link to="/pro-carer-circle" className="mt-4 inline-flex items-center gap-1 text-purple font-medium text-sm">See Pro toolkit <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-surface">
        <div className="container-jcc grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <SectionHeading eyebrow="A different relationship with work" accent="purple"
              title="You're not a shift. You're a professional."
              description="Independent care is more than just picking up private shifts. It's a proper practice, with standards, records and fair pricing. JCC gives you the structure to run it that way — without the overhead of an agency." />
            <div className="mt-6"><DisclaimerBar /></div>
          </div>
          <div className="rounded-3xl border border-slate-200 p-8 bg-white">
            <div className="overline text-purple mb-3">Inside the Pro Carer Office</div>
            <h3 className="font-display text-3xl font-bold">£19<span className="text-base font-normal text-ink-muted"> / month</span></h3>
            <p className="mt-2 text-sm font-medium text-purple">Less than most agencies take from a single shift.</p>
            <p className="mt-2 text-sm text-ink-muted">Practical tools, guidance and protection for carers working independently.</p>
            <ul className="mt-6 space-y-3 text-sm">
              <li className="flex gap-2"><ShieldCheck className="w-5 h-5 text-purple shrink-0" />Shift Toolkit + professional templates</li>
              <li className="flex gap-2"><ShieldCheck className="w-5 h-5 text-purple shrink-0" />Contracts, pricing &amp; payment protection</li>
              <li className="flex gap-2"><ShieldCheck className="w-5 h-5 text-purple shrink-0" />Documentation systems &amp; incident records</li>
              <li className="flex gap-2"><ShieldCheck className="w-5 h-5 text-purple shrink-0" />Tax toolkit + self-employment guidance</li>
              <li className="flex gap-2"><ShieldCheck className="w-5 h-5 text-purple shrink-0" />Boundary setting &amp; safer working support</li>
              <li className="flex gap-2"><ShieldCheck className="w-5 h-5 text-purple shrink-0" />CPD tracker + professional development tools</li>
              <li className="flex gap-2"><ShieldCheck className="w-5 h-5 text-purple shrink-0" />Real-world guidance for independent care work</li>
            </ul>
            <a
              href="/membership/pro-carer-circle"
              className="btn-purple mt-8 w-full"
            >
              Start Your Free 7-Day Trial
            </a>
            <Link to="/compare-memberships#carer-memberships" className="mt-3 block text-center text-xs text-ink-soft hover:text-purple underline">
              Compare all carer memberships
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
